import { useMemo, useState } from "react";
import {
  IconBriefcase,
  IconChevronLeft,
  IconDownload,
  IconFilter,
  IconLock,
  IconMapPin,
  IconPlus,
  IconSearch,
  IconStar,
  IconUsers,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { useAllCustomers } from "./useAllCustomers";
import { Page, PageHeader } from "@hana/shell";
import {
  Button,
  Segmented,
  Select,
  StateView,
  Skeleton,
  useToast,
  type ScreenState,
} from "@hana/ui";
import {
  ALL_PROV,
  LEADS_IPAD,
  LEADS_PHONE,
  PARTNERS_IPAD,
  PHONE_LIMIT,
  PROVINCES,
  QUICK,
  compactRow,
  filterCompact,
  flagOf,
  type QuickKey,
} from "./compact";
import { searchCustomers } from "./logic";
import { abbr, initials } from "./view";
import s from "./customerCompact.module.css";
import { useT } from '@hana/i18n';
import './i18n';

// Mint-Customers-iPad (khung 1: danh sách) · Mint-Customers-Phone (khung 1). Chỉ đọc; nút ghi báo toast.
// Tiền (hạn mức, công nợ) ẩn: dòng khách chỉ có cờ trạng thái, không số tiền.

type Tab = "cus" | "lead" | "partner";

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get("state");
  return v === "loading" || v === "empty" || v === "error" || v === "noperm"
    ? v
    : null;
}

export function CustomerCompact({ phone }: { phone: boolean }) {
  const t = useT();
  const toast = useToast();
  const { data, isLoading, isError, refetch } = useAllCustomers();
  const forced = readState();
  const [tab, setTab] = useState<Tab>("cus");
  const [q, setQ] = useState("");
  const [quick, setQuick] = useState<ReadonlySet<QuickKey>>(new Set());
  const [prov, setProv] = useState<string>(ALL_PROV);
  const [fltOpen, setFltOpen] = useState(false);

  const all = useMemo(
    () => (data?.items ?? []).slice(0, phone ? PHONE_LIMIT : undefined),
    [data, phone],
  );
  const base = useMemo(() => all.map(compactRow), [all]);
  const rows = useMemo(() => {
    const keep = new Set(searchCustomers(all, q).map((c) => c.id));
    return filterCompact(base, quick, prov).filter((r) => keep.has(r.c.id));
  }, [all, base, q, quick, prov]);

  const noWrite = () => toast({ msg: t('crm.cc.noWrite') });
  const pickTab = (k: Tab) =>
    k === "cus" ? setTab(k) : toast({ msg: t('crm.cc.tabClosed') });
  const toggle = (k: QuickKey) =>
    setQuick((p) => {
      const n = new Set(p);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  let state: ScreenState = "ready";
  if (forced) state = forced;
  else if (isLoading) state = "loading";
  else if (isError) state = "error";
  else if (all.length === 0) state = "empty";
  const ready = state === "ready";

  const tabs: { key: Tab; label: string; n?: number }[] = [
    {
      key: "cus",
      label: t('crm.cc.tabCus'),
      n: ready ? all.length : undefined,
    },
    { key: "lead", label: t('crm.cc.tabLead'), n: phone ? LEADS_PHONE : LEADS_IPAD },
    ...(phone
      ? []
      : [{ key: "partner" as const, label: t('crm.cc.tabPartner'), n: PARTNERS_IPAD }]),
  ];
  const nFilter = quick.size + (prov === ALL_PROV ? 0 : 1);
  const chips = (
    <div className={s.chips} role="group" aria-label={t('crm.at.2')}>
      {QUICK.map((f) => (
        <button
          key={f.key}
          type="button"
          className={s.chip}
          aria-pressed={quick.has(f.key)}
          onClick={() => toggle(f.key)}
        >
          {f.label} <span>{base.filter(f.test).length}</span>
        </button>
      ))}
    </div>
  );
  const provSel = (
    <Select
      aria-label={t('crm.at.3')}
      value={prov}
      onChange={(e) => setProv(e.target.value)}
      options={PROVINCES}
    />
  );
  const csv = (
    <Button
      variant="secondary"
      icon={<IconDownload size={16} aria-hidden="true" />}
      onClick={() =>
        toast({
          msg: t('crm.cc.exported', { n: rows.length }),
        })
      }
    >
      {t('crm.x.exportCsv')}
    </Button>
  );

  return (
    <Page kind="table">
      <PageHeader
        title={t('crm.at.4')}
        lead={
          phone ? (
            <a className={s.back} href="/more" aria-label={t('crm.at.5')}>
              <IconChevronLeft size={22} aria-hidden="true" />
            </a>
          ) : undefined
        }
        actions={
          phone ? (
            <>
              <button
                type="button"
                className={s.tool}
                aria-label={t('crm.at.6')}
                onClick={() => setFltOpen((v) => !v)}
              >
                <IconFilter size={19} aria-hidden="true" />
                {nFilter > 0 && <span className={s.badge}>{nFilter}</span>}
              </button>
              <Button
                variant="primary"
                aria-label={t('crm.at.7')}
                onClick={noWrite}
              >
                <IconPlus size={20} aria-hidden="true" />
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              icon={<IconPlus size={16} aria-hidden="true" />}
              onClick={noWrite}
            >
              {t('crm.x.add')}
            </Button>
          )
        }
      />
      <div className={s.page}>
        <label className={s.search}>
          <IconSearch size={17} aria-hidden="true" />
          <input
            aria-label={t('crm.at.8')}
            placeholder={t('crm.at.9')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <Segmented label={t('crm.at.10')} value={tab} onChange={pickTab} items={tabs} />
        {ready && tab === "cus" && !phone && (
          <>
            {chips}
            <div className={s.provRow}>
              {provSel}
              <span className={s.count}>{t('crm.cc.count', { n: rows.length })}</span>
              {csv}
            </div>
          </>
        )}
        {ready && phone && fltOpen && (
          <div className={s.fltPanel}>
            {chips}
            {provSel}
            {csv}
          </div>
        )}
        <StateView
          state={ready && rows.length === 0 ? "empty" : state}
          title={
            ready
              ? t('crm.cc.notFound')
              : state === "empty"
                ? t('crm.cc.noCus')
                : undefined
          }
          desc={ready ? t('crm.cc.noMatch') : undefined}
          icon={
            ready || state === "empty" ? (
              <IconUsers size={26} stroke={1.8} />
            ) : undefined
          }
          action={
            state === "error" ? (
              <Button onClick={() => void refetch()}>{t('crm.retry')}</Button>
            ) : undefined
          }
          skeleton={<Skeleton rows={6} />}
        >
          <section aria-label={phone ? undefined : t('crm.cc.listLabel')}>
            <ul className={s.list}>
              {rows.map(({ c, prov: pv, cls }) => {
                const flag = flagOf(c);
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      className={s.row}
                      onClick={() =>
                        toast({ msg: t('crm.cc.detailClosed') })
                      }
                    >
                      <span className={s.sq} aria-hidden="true">
                        {initials(c.id)}
                      </span>
                      <span className={s.rowTx}>
                        <span className={s.l1}>
                          <b className={s.name}>{c.id}</b>
                          <span
                            className={s.meta}
                            title={t('crm.cc.jobs90', { n: c.jobs })}
                          >
                            <IconBriefcase size={14} aria-hidden="true" />
                            {c.jobs}
                          </span>
                        </span>
                        <span className={s.l2}>
                          <span className={s.meta} title={`Sale ${c.sale}`}>
                            <span className={s.av} aria-hidden="true">
                              {abbr(c.sale)}
                            </span>
                            {c.sale}
                          </span>
                          <span className={`${s.meta} ${s.shrink}`} title={pv}>
                            <IconMapPin size={13} aria-hidden="true" />
                            <span className={s.ell}>{pv}</span>
                          </span>
                          <span className={s.meta} title={t('crm.cc.rank', { v: cls })}>
                            <IconStar size={13} aria-hidden="true" />
                            {cls}
                          </span>
                          {flag && (
                            <span
                              className={`${s.flag} ${flag.over ? s.err : s.warn}`}
                              title={flag.text}
                            >
                              <IconAlertTriangle size={12} aria-hidden="true" />
                              {flag.text}
                              {phone && flag.over && (
                                <>
                                  <IconLock size={12} aria-hidden="true" />
                                  DO
                                </>
                              )}
                            </span>
                          )}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        </StateView>
      </div>
    </Page>
  );
}
